import React, {useEffect, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {useParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../utils/project-utils.js";
import Swal from "sweetalert2";
import adrCodeService from "../../../../services/web/adr-code-service.js";
import {FaPlus} from "react-icons/fa";
import AdrCodePreview from "../../../../components/adr-code-preview/adr-code-preview.jsx";


const defaultInsertDTO = {
    "code": null,
    "description": null,
}

const i18nErrors = {
    "adr.code.empty": 'O campo "Painel de segurança" é obrigatório.',
    "adr.code.invalid.size": "O painel de segurança precisa conter entre 6 e 8 caracteres.",
    "adr.description.empty": 'O campo "Descrição" é obrigatório.',
    "adr.code.already.exists": "Esse painel de segurança já existe!",
    "monitoring.with.reference": "Já existe um monitoramento cadastrado com esse painel de segurança!",
}

const i18nErrorsMap = Object.fromEntries(
    Object.entries(i18nErrors).map(([key, value]) => [key, {message: value}])
);

function AdrCodeForm() {

    const params = useParams();
    const rawId = params?.id;
    const navigate = navigateHook();

    const [currentId, setCurrentId] = useState(rawId && rawId !== "null" ? Number(rawId) : null);
    const [filterValues, setFilterValues] = useState(defaultInsertDTO);
    const [isSaving, setIsSaving] = useState(false);

    const isEditMode = !!currentId;

let {data: adrCode, isLoading: isLoadingMonitoring, refetch} = useQuery({
        queryFn: () => {
            return adrCodeService.get(currentId)
        },
        queryKey: ["monitoring", {}],
        enabled: !!currentId
    });

    useEffect(() => {
       if (adrCode) {
           setFilterValues({
               "code": adrCodeService.formatAdrCode(adrCode?.code),
               "description": adrCode?.description,
           })
       }
    }, [adrCode]);

    const validate = () => {

        if (!filterValues?.code) {
            return 'O campo "Painel de segurança" é obrigatório.'
        }

        if(!filterValues?.description){
            return 'O campo "Descrição" é obrigatório.'
        }

        return null;
    };

    const handleSave = async () => {
        if (isSaving) return;

        const err = validate();

        if (err) {
            projectUtils.swalErrorMessage('', err, 'Ok');
            return;
        }

        setIsSaving(true);

        const payload = {
            "code": adrCodeService.normalizeAdrCode(filterValues?.code),
            "description": filterValues?.description,
        }

        const request = isEditMode
            ? adrCodeService.update(currentId, payload)
            : adrCodeService.create(payload);

        try {

            const data = await request;
            const newId = isEditMode ? currentId : (data?.id ?? data);

            projectUtils.success(!isEditMode);

            if (isEditMode) {
                await refetch();
            } else {
                let urlDev = `/adr-code/form/${newId}`;
                navigate(urlDev);
            }

        } catch (error) {

            let message = "Não foi possível salvar.";

            if (error.response?.data && error.response?.data?.messageKey) {
                message = i18nErrorsMap[error.response?.data?.messageKey]?.message
            }

            projectUtils.swalErrorMessage("", message, "Ok");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = () => {
        if (!currentId) return;

        Swal.fire({
            title: "Excluir",
            text: `Excluir o painel de segurança?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#063A6C",
            confirmButtonText: "Sim",
            cancelButtonColor: "#c10007",
            cancelButtonText: "Não",
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                return adrCodeService
                    .remove(currentId)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        let urlDev = `/adr-code/`;
                        navigate(urlDev);
                    })
                    .catch((error) => {
                        console.error(error);
                        let message = "Não foi possível excluir.";

                        if (error.response?.status !== 500) {
                            message = error.response?.data?.message || message;
                        }
                        projectUtils.swalErrorMessage("", message, "Ok");
                    });

            }
        });
    };

    const onChange = ({target}) => {
        let {id, value} = target;
        if(id === "code") {
            value = adrCodeService.formatAdrCode(value);
        }
        setFilterValues(prev => ({...prev, [id]: value}));
    }

    const newAdrCode = () => {
        const urlDev = "/adr-code/form";
        navigate(urlDev);
    };

    return (
        <Template isLoading={isLoadingMonitoring || isSaving}>
            <Template.Header>
                <Template.Title>Painel de segurança</Template.Title>
                {currentId && (
                    <Button onClick={newAdrCode} target="primary-outline"><FaPlus />Adicionar painel de segurança</Button>
                )}
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col h-full  min-h-0">
                <Card>

                    <Card.Body className="flex flex-col gap-6 ">
                        <div className="flex gap-4 w-full items-end pt-2">
                            <InputForm
                                id="code"
                                label="Painel de segurança *"
                                value={filterValues?.code ?? ""}
                                onChange={onChange}
                                className={'active'}
                            />
                            <InputForm
                                maxLength={200}
                                id="description"
                                label="Descrição *"
                                classNameWrapper="flex-1"
                                value={filterValues?.description ?? ""}
                                onChange={onChange}
                                className={'active'}
                            />
                        </div>
                        <div className="flex gap-4 w-full">
                            <AdrCodePreview code={filterValues?.code} description={filterValues?.description} />
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex gap-2 justify-end w-full">
                                {isEditMode && (
                                    <Button onClick={handleDelete} target="danger" actionFeedbackType='delete'>Excluir</Button>
                                )}
                                <Button onClick={handleSave} target="primary"

                                        disabled={isSaving || isLoadingMonitoring} actionFeedbackType='save'>Salvar</Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

            </Template.Body>
        </Template>
    )
}

export default AdrCodeForm;